import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../context/AuthContext';
import { apiService } from '../../services/api';
import { getUserProfile } from '../../utils/userIdentities';
import { orderDisplayNumber } from '../../utils/orderDisplay';
import {
  MAX_SUPPORT_MESSAGE_LENGTH,
  REASONS_WITH_MESSAGE,
  SUPPORT_REASONS,
} from './supportConstants';

const ORDER_STATUS_LABELS = {
  pending: 'Ожидает',
  paid: 'Оплачен',
  confirmed: 'Подтверждён',
  awaiting_confirmation: 'Уточнение',
  preparing: 'Готовится',
  ready: 'Готов',
  completed: 'Выполнен',
  cancelled: 'Отменён',
};

const ORDER_STATUS_COLORS = {
  pending: '#ffa500',
  paid: '#1fb9c4',
  confirmed: '#9c27b0',
  awaiting_confirmation: '#ff9800',
  preparing: '#2196f3',
  ready: '#4caf50',
  completed: '#9e9e9e',
  cancelled: '#f44336',
};

function formatOrderDate(order) {
  if (!order?.created_at) return '';
  return new Date(order.created_at).toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatOrderPrice(order) {
  if (order?.total_amount == null) return '';
  return `${Math.round(Number(order.total_amount))} ₽`;
}

const SupportRequestModal = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const profile = getUserProfile(user);
  const orderListRef = useRef(null);

  const [contactName, setContactName] = useState('');
  const [orderId, setOrderId] = useState('');
  const [reason, setReason] = useState('question');
  const [message, setMessage] = useState('');
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersOpen, setOrdersOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const showMessageField = REASONS_WITH_MESSAGE.includes(reason);

  const selectedOrder = useMemo(
    () => orders.find((o) => String(o.id) === String(orderId)) || null,
    [orders, orderId],
  );

  const resetForm = useCallback(() => {
    setContactName(profile?.name || '');
    setOrderId('');
    setReason('question');
    setMessage('');
    setOrdersOpen(false);
    setError(null);
    setSuccess(false);
  }, [profile?.name]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    resetForm();
  }, [isOpen, resetForm]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    let cancelled = false;

    const loadOrders = async () => {
      setOrdersLoading(true);
      try {
        const data = await apiService.getUserOrders();
        if (!cancelled) {
          const sorted = [...(data || [])].sort(
            (a, b) => new Date(b.created_at) - new Date(a.created_at),
          );
          setOrders(sorted);
        }
      } catch (err) {
        if (!cancelled) {
          console.warn('[Support] Failed to load orders:', err);
          setOrders([]);
        }
      } finally {
        if (!cancelled) {
          setOrdersLoading(false);
        }
      }
    };

    loadOrders();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!ordersOpen) return undefined;
    const onDocClick = (e) => {
      if (orderListRef.current && !orderListRef.current.contains(e.target)) {
        setOrdersOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [ordersOpen]);

  const messageLength = message.length;

  const canSubmit = useMemo(() => {
    if (!contactName.trim()) {
      return false;
    }
    if (showMessageField) {
      const trimmed = message.trim();
      return trimmed.length > 0 && trimmed.length <= MAX_SUPPORT_MESSAGE_LENGTH;
    }
    return true;
  }, [contactName, message, showMessageField]);

  const handleSelectOrder = (id) => {
    setOrderId(id);
    setOrdersOpen(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit || submitting) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const payload = {
        contact_name: contactName.trim(),
        order_id: orderId ? Number(orderId) : null,
        reason,
      };
      if (showMessageField) {
        payload.message = message.trim();
      }

      await apiService.submitSupportRequest(payload);
      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 1200);
    } catch (err) {
      setError(err.message || 'Не удалось отправить обращение');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  const modalContent = (
    <div className="modal-overlay support-modal" onClick={onClose}>
      <div
        className="modal-container"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="support-modal-title"
        aria-modal="true"
      >
        <div className="modal-header">
          <h2 id="support-modal-title">Обращение в поддержку</h2>
          <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Закрыть">
            ×
          </button>
        </div>

        <form className="modal-body support-form" onSubmit={handleSubmit}>
          <p className="modal-description">
            Опишите вопрос или проблему. Отмена заказа через эту форму не выполняется автоматически —
            мы свяжемся с вами.
          </p>

          {success && (
            <p className="support-form-success" role="status">
              Обращение отправлено. Спасибо!
            </p>
          )}

          {error && (
            <p className="support-form-error" role="alert">
              {error}
            </p>
          )}

          <div className="support-form-field">
            <label htmlFor="support-contact-name">Имя</label>
            <input
              id="support-contact-name"
              type="text"
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              maxLength={100}
              autoComplete="name"
              required
            />
          </div>

          <div className="support-form-field" ref={orderListRef}>
            <span id="support-order-label">Заказ</span>
            <button
              type="button"
              id="support-order"
              className={`support-order-trigger${ordersOpen ? ' is-open' : ''}`}
              aria-haspopup="listbox"
              aria-expanded={ordersOpen}
              aria-labelledby="support-order-label"
              disabled={ordersLoading}
              onClick={() => setOrdersOpen((v) => !v)}
            >
              {ordersLoading ? (
                <span className="support-order-trigger-main">Загрузка заказов…</span>
              ) : selectedOrder ? (
                <>
                  <span className="support-order-trigger-main">
                    <span className="support-order-trigger-title">
                      #{orderDisplayNumber(selectedOrder)}
                    </span>
                    <span className="support-order-trigger-meta">
                      {formatOrderDate(selectedOrder)}
                      {formatOrderPrice(selectedOrder)
                        ? ` · ${formatOrderPrice(selectedOrder)}`
                        : ''}
                    </span>
                  </span>
                  <span
                    className="support-order-status"
                    style={{
                      backgroundColor:
                        ORDER_STATUS_COLORS[selectedOrder.status] || '#8d6e63',
                    }}
                  >
                    {ORDER_STATUS_LABELS[selectedOrder.status] || selectedOrder.status}
                  </span>
                </>
              ) : (
                <span className="support-order-trigger-main support-order-trigger-placeholder">
                  Не привязан к заказу
                </span>
              )}
              <span className="support-order-chevron" aria-hidden>
                ▾
              </span>
            </button>

            {ordersOpen && (
              <div
                className="support-order-dropdown"
                role="listbox"
                aria-labelledby="support-order-label"
              >
                <button
                  type="button"
                  role="option"
                  aria-selected={!orderId}
                  className={`support-order-option${!orderId ? ' is-selected' : ''}`}
                  onClick={() => handleSelectOrder('')}
                >
                  <span className="support-order-option-title">Не привязан к заказу</span>
                  <span className="support-order-option-meta">Общий вопрос без заказа</span>
                </button>

                {orders.length === 0 ? (
                  <div className="support-order-empty">Нет заказов для выбора</div>
                ) : (
                  orders.map((order) => {
                    const selected = String(orderId) === String(order.id);
                    return (
                      <button
                        key={order.id}
                        type="button"
                        role="option"
                        aria-selected={selected}
                        className={`support-order-option${selected ? ' is-selected' : ''}`}
                        onClick={() => handleSelectOrder(String(order.id))}
                      >
                        <span className="support-order-option-top">
                          <span className="support-order-option-title">
                            #{orderDisplayNumber(order)}
                          </span>
                          <span
                            className="support-order-status"
                            style={{
                              backgroundColor:
                                ORDER_STATUS_COLORS[order.status] || '#8d6e63',
                            }}
                          >
                            {ORDER_STATUS_LABELS[order.status] || order.status}
                          </span>
                        </span>
                        <span className="support-order-option-meta">
                          {formatOrderDate(order)}
                          {formatOrderPrice(order) ? ` · ${formatOrderPrice(order)}` : ''}
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </div>

          <div className="support-form-field">
            <span id="support-reason-label">Причина обращения</span>
            <div className="support-reason-options" role="radiogroup" aria-labelledby="support-reason-label">
              {SUPPORT_REASONS.map((item) => (
                <label key={item.value} className="support-reason-option">
                  <input
                    type="radio"
                    name="support-reason"
                    value={item.value}
                    checked={reason === item.value}
                    onChange={() => setReason(item.value)}
                  />
                  <span>{item.label}</span>
                </label>
              ))}
            </div>
          </div>

          {showMessageField && (
            <div className="support-form-field">
              <label htmlFor="support-message">
                {reason === 'review' ? 'Отзыв' : 'Комментарий'}
              </label>
              <textarea
                id="support-message"
                value={message}
                onChange={(e) => setMessage(e.target.value.slice(0, MAX_SUPPORT_MESSAGE_LENGTH))}
                maxLength={MAX_SUPPORT_MESSAGE_LENGTH}
                placeholder={
                  reason === 'review'
                    ? 'Поделитесь впечатлениями...'
                    : 'Опишите вопрос или ситуацию...'
                }
                required
              />
              <div className="support-message-counter">
                {messageLength}/{MAX_SUPPORT_MESSAGE_LENGTH}
              </div>
            </div>
          )}

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>
              Закрыть
            </button>
            <button type="submit" className="btn btn-primary" disabled={!canSubmit || submitting || success}>
              {submitting ? 'Отправка…' : 'Отправить'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  if (typeof document !== 'undefined' && document.body) {
    return createPortal(modalContent, document.body);
  }

  return modalContent;
};

export default SupportRequestModal;
